/**
 * BulkLinkMatchHubModal — round 2.
 *
 * Two-pane Drawer mirrors BulkLinkTeamModal:
 *
 *   Pane 1 (team picker):
 *     - searchTeams() for local Teams; only Teams with `external_id` set are
 *       valid targets (unlinked rows greyed out with a tooltip).
 *     - On pick → loads bulkLinkMatchHubSuggest(team_id) → renders the
 *       side-by-side suggestion list.
 *
 *   Pane 2 (suggestion list):
 *     - Per local Person: shows MatchHub-suggested candidate side-by-side with
 *       comparison fields (DoB / jersey / position). Editor edits the
 *       confirmed_name per row, checks rows to confirm, and submits.
 *     - Per-row manual fallback search opens an inline MatchHubSearchInput
 *       hitting search_matchhub_players (constrained to the team's roster).
 *     - Submit → bulkLinkMatchHubConfirm() → result phase with per-row badges.
 *
 * Always confirms; the suggestion side never silently auto-creates.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  searchTeams,
  bulkLinkMatchHubSuggest,
  bulkLinkMatchHubConfirm,
  searchMatchHubPlayers,
} from '../../../../api';
import { Btn, Drawer } from '../../../../shared/components/redesign';
import MatchHubSearchInput from '../../../../shared/components/MatchHubSearchInput';
import styles from './BulkLinkMatchHubModal.module.css';

const DEBOUNCE_MS = 250;

export default function BulkLinkMatchHubModal({ onClose, onComplete }) {
  // Team picker (pane 1)
  const [teamQuery, setTeamQuery] = useState('');
  const [teamSuggestions, setTeamSuggestions] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const teamTimer = useRef(null);

  // Suggestion list (pane 2)
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const [suggestData, setSuggestData] = useState(null); // {team, rows, unmatched_roster, unmatched_reason?}
  const [rowChoices, setRowChoices] = useState({}); // {person_id: {checked, matchhub_player_id, confirmed_name}}
  const [error, setError] = useState(null);

  // Submit + result (pane 3)
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);

  // Team search (debounced) — only local Teams (already known to operator).
  useEffect(() => {
    if (teamTimer.current) clearTimeout(teamTimer.current);
    if (selectedTeam && teamQuery === selectedTeam.name) {
      setTeamSuggestions([]);
      return undefined;
    }
    if (!teamQuery || teamQuery.length < 2) {
      setTeamSuggestions([]);
      return undefined;
    }
    teamTimer.current = setTimeout(async () => {
      try {
        const res = await searchTeams(teamQuery);
        setTeamSuggestions(res || []);
      } catch (err) {
        console.error('Local team search failed:', err);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(teamTimer.current);
  }, [teamQuery, selectedTeam]);

  const pickTeam = useCallback(async (team) => {
    if (!team?.external_id) {
      setError(
        'This team has no MatchHub link yet. Link the team first from Manage > Teams.',
      );
      return;
    }
    setSelectedTeam(team);
    setTeamQuery(team.name);
    setTeamSuggestions([]);
    setError(null);
    setLoadingSuggest(true);
    try {
      const data = await bulkLinkMatchHubSuggest(team.id);
      setSuggestData(data);
      // Seed row choices: every row pre-selected if there's a suggestion, with
      // confirmed_name = matchhub's name (editor edits as needed).
      const initial = {};
      for (const r of data.rows || []) {
        initial[r.person.id] = {
          checked: !!r.suggested,
          matchhub_player_id: r.suggested?.provider_id || null,
          confirmed_name: r.suggested?.name || r.person.display_name,
        };
      }
      setRowChoices(initial);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load suggestions');
      setSuggestData(null);
    } finally {
      setLoadingSuggest(false);
    }
  }, []);

  const clearTeam = () => {
    setSelectedTeam(null);
    setTeamQuery('');
    setTeamSuggestions([]);
    setSuggestData(null);
    setRowChoices({});
    setError(null);
  };

  const toggleRow = (personId) => {
    setRowChoices((prev) => ({
      ...prev,
      [personId]: { ...prev[personId], checked: !prev[personId]?.checked },
    }));
  };

  const updateRowChoice = (personId, partial) => {
    setRowChoices((prev) => ({
      ...prev,
      [personId]: { ...prev[personId], ...partial },
    }));
  };

  const handleSubmit = useCallback(async () => {
    if (!suggestData) return;
    const rows = (suggestData.rows || [])
      .filter((r) => rowChoices[r.person.id]?.checked)
      .map((r) => ({
        person_id: r.person.id,
        matchhub_player_id: rowChoices[r.person.id]?.matchhub_player_id,
        confirmed_name: (rowChoices[r.person.id]?.confirmed_name || '').trim(),
      }))
      .filter((row) => row.matchhub_player_id && row.confirmed_name);

    if (rows.length === 0) {
      setError('Tick at least one row with a chosen MatchHub player and confirmed name.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const data = await bulkLinkMatchHubConfirm(rows);
      setResults(data);
      if (onComplete) onComplete(data);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  }, [suggestData, rowChoices, onComplete]);

  // ---- Result phase ------------------------------------------------------
  if (results) {
    const personById = new Map(
      (suggestData?.rows || []).map((r) => [r.person.id, r.person]),
    );
    const body = (
      <>
        <p className={styles.hint}>
          {`Linked ${results.summary.linked}, errored ${results.summary.errored}, skipped ${results.summary.skipped} of ${results.summary.total}.`}
        </p>
        <div className={styles.resultList}>
          {results.results.map((r) => {
            const person = personById.get(r.person_id);
            return (
              <div key={r.person_id} className={styles.resultRow}>
                <span className={styles.resultName}>
                  {person?.display_name || r.person_id}
                </span>
                <span
                  className={clsx(styles.statusBadge, styles[`status_${r.status}`])}
                >
                  {r.status}
                </span>
                {r.error && <span className={styles.resultError}>{r.error}</span>}
              </div>
            );
          })}
        </div>
      </>
    );
    return (
      <Drawer
        open
        onClose={onClose}
        eyebrow="§ BULK LINK MATCHHUB"
        title="Sync complete"
        subtitle={suggestData?.team?.name}
        ariaLabel="MatchHub sync results"
        body={body}
        footer={(
          <>
            <span className={styles.footerSpacer} />
            <Btn kind="primary" size="sm" onClick={onClose}>
              Done
            </Btn>
          </>
        )}
      />
    );
  }

  // ---- Suggestion phase --------------------------------------------------
  const body = (
    <>
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.formGroup}>
        <label className={styles.label}>Linked team</label>
        <div className={styles.teamPicker}>
          <input
            type="text"
            className={styles.input}
            placeholder="Search a team you've already linked to MatchHub…"
            value={teamQuery}
            onChange={(e) => setTeamQuery(e.target.value)}
            autoFocus
          />
          {selectedTeam && (
            <Btn kind="ghost" size="sm" onClick={clearTeam}>
              Clear
            </Btn>
          )}
        </div>
        {teamSuggestions.length > 0 && (
          <ul className={styles.suggestList}>
            {teamSuggestions.map((t) => (
              <li key={t.id}>
                <button
                  type="button"
                  className={clsx(
                    styles.suggestItem,
                    !t.external_id && styles.suggestItemDisabled,
                  )}
                  onClick={() => pickTeam(t)}
                  disabled={!t.external_id}
                  title={!t.external_id ? 'Link this team to MatchHub first' : ''}
                >
                  <span>{t.name}</span>
                  <span className={styles.teamTypeChip}>
                    {t.external_id ? `MH ${t.external_id}` : 'not linked'}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {loadingSuggest && <p className={styles.hint}>Loading roster comparison…</p>}

      {suggestData?.unmatched_reason === 'no_roster_synced' && (
        <div className={styles.error}>
          {suggestData.hint || 'No MatchHub roster cached for this team yet.'}
        </div>
      )}

      {suggestData && suggestData.rows && suggestData.rows.length > 0 && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Candidate matches ({suggestData.rows.length})
          </label>
          <div className={styles.rowList}>
            {suggestData.rows.map((r) => {
              const choice = rowChoices[r.person.id] || {};
              return (
                <div key={r.person.id} className={styles.row}>
                  <input
                    type="checkbox"
                    checked={!!choice.checked}
                    onChange={() => toggleRow(r.person.id)}
                  />
                  <div className={styles.rowBody}>
                    <div className={styles.rowSide}>
                      <div className={styles.rowName}>{r.person.display_name}</div>
                      <div className={styles.rowMeta}>
                        {[
                          r.person.current_club_name,
                          r.person.jersey_number != null
                            ? `#${r.person.jersey_number}`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </div>
                    <div className={styles.rowArrow}>→</div>
                    <div className={styles.rowSide}>
                      {r.suggested ? (
                        <>
                          <div className={styles.rowName}>{r.suggested.name}</div>
                          <div className={styles.rowMeta}>
                            {[
                              r.suggested.position,
                              r.suggested.birth_date,
                              `score ${r.suggested.score}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        </>
                      ) : (
                        <div className={styles.rowMeta}>(no candidate; search manually)</div>
                      )}
                    </div>
                  </div>
                  <input
                    type="text"
                    className={styles.input}
                    value={choice.confirmed_name || ''}
                    onChange={(e) =>
                      updateRowChoice(r.person.id, { confirmed_name: e.target.value })
                    }
                    placeholder="Confirmed name (override)"
                  />
                  <details className={styles.rowFallback}>
                    <summary>Wrong / no match — search manually</summary>
                    <MatchHubSearchInput
                      searchFn={(q) =>
                        searchMatchHubPlayers(q, {
                          teamExternalId: suggestData.team?.external_id,
                        })
                      }
                      dataKey="players"
                      renderRow={(p) => (
                        <div style={{ padding: '4px 8px' }}>
                          <div style={{ fontSize: 13 }}>{p.name}</div>
                          <div style={{ fontSize: 11, opacity: 0.6 }}>
                            {[p.position, p.birth_date].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      )}
                      onSelect={(p) =>
                        updateRowChoice(r.person.id, {
                          matchhub_player_id: p.id,
                          confirmed_name: p.name,
                          checked: true,
                        })
                      }
                      placeholder="Search MatchHub for the right player…"
                      initialQuery={r.person.last_name || ''}
                      inputClassName={styles.input}
                    />
                  </details>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {suggestData && (suggestData.unmatched_roster?.length || 0) > 0 && (
        <div className={styles.formGroup}>
          <label className={styles.label}>
            MatchHub players not in Matrix yet ({suggestData.unmatched_roster.length})
          </label>
          <p className={styles.hint}>
            These rosters are visible on MatchHub but you don't have a local Person row.
            Create them from the face-tagging flow when they appear in a photo (round 2 Goal 3.4).
          </p>
          <ul className={styles.suggestList}>
            {suggestData.unmatched_roster.slice(0, 50).map((p) => (
              <li key={p.provider_id}>
                <span>{p.name}</span>
                <span className={styles.teamTypeChip}>{p.position || ''}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );

  return (
    <Drawer
      open
      onClose={onClose}
      eyebrow="§ BULK LINK MATCHHUB"
      title="Sync unlinked persons with MatchHub"
      subtitle="Every match is editor-confirmed; nothing auto-links."
      ariaLabel="Bulk-link persons with MatchHub"
      body={body}
      footer={(
        <>
          <span className={styles.footerSpacer} />
          <Btn kind="ghost" size="sm" onClick={onClose}>
            Cancel
          </Btn>
          <Btn
            kind="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting || !suggestData || (suggestData.rows || []).length === 0}
          >
            {submitting ? 'Linking…' : 'Confirm selected'}
          </Btn>
        </>
      )}
    />
  );
}
